import PropTypes from "prop-types";
import React from "react";
import {
  Button,
  ButtonToolbar,
  Col,
  Form,
  Row,
  DropdownButton,
  Dropdown,
} from "react-bootstrap";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import UploadBase64 from "../../../../components/imageUploader/ImageUploader.jsx";
import RichTextEditor from "../../../../components/richTextEditor/RichTextEditor.jsx";
import VimeoUpload from "../../../../components/VimeoUpload.jsx";
import { selectVimeoAccessToken } from "../../../../store/selectors.js";
import genericTranslations from "../../../../utils/genericTranslations.js";

const messages = {
  content: {
    id: "modules.ArriVideo.components.ContentItemForm.content",
    defaultMessage: "Content",
  },
  title: {
    id: "modules.ArriVideo.components.ContentItemForm.title",
    defaultMessage: "Title",
  },
  type: {
    id: "modules.ArriVideo.components.ContentItemForm.type",
    defaultMessage: "Type",
  },
  typeHtml: {
    id: "modules.ArriVideo.components.ContentItemForm.typeHtml",
    defaultMessage: "HTML",
  },
  typeImage: {
    id: "modules.ArriVideo.components.ContentItemForm.typeImage",
    defaultMessage: "Image",
  },
  typeVideo: {
    id: "modules.ArriVideo.components.ContentItemForm.typeVideo",
    defaultMessage: "Video",
  },
  upload: {
    id: "modules.ArriVideo.components.ContentItemForm.uploadButtonText",
    defaultMessage: "Upload image",
  },
};

const ContentItemForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  onClickDelete,
  setFieldValue,
  values,
  vimeoArriVideoProjectId,
}) => {
  const intl = useIntl();

  const vimeoAccessToken = useSelector(selectVimeoAccessToken);

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col xs={12} md={9} lg={9}>
          <Form.Group controlId="title" className="mb-3">
            <Form.Label>{intl.formatMessage(messages.title)}</Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              value={values.title || ""}
              onChange={handleChange}
            />
            {errors.title && (
              <Form.Text className="text-danger">{errors.title}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={9} lg={9}>
          <Form.Group controlId="type" className="mb-3">
            <Form.Label>{intl.formatMessage(messages.type)}</Form.Label>
            <br />
            <DropdownButton
              id="typeDropdown"
              variant="secondary"
              title={
                values.type
                  ? intl.formatMessage(
                      messages[
                        `type${
                          values.type.charAt(0).toUpperCase() +
                          values.type.slice(1)
                        }`
                      ],
                    )
                  : intl.formatMessage(messages.type)
              }
              onSelect={(type) => {
                setFieldValue("content", "");
                setFieldValue("type", type);
              }}
            >
              {["html", "image", "video"].map((type) => (
                <Dropdown.Item key={`type-${type}`} eventKey={type}>
                  {intl.formatMessage(
                    messages[
                      `type${type.charAt(0).toUpperCase() + type.slice(1)}`
                    ],
                  )}
                </Dropdown.Item>
              ))}
            </DropdownButton>
            {errors.type && (
              <Form.Text className="text-danger">{errors.type}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={9} lg={9}>
          <Form.Group controlId="content" className="mb-3">
            <Form.Label>{intl.formatMessage(messages.content)}</Form.Label>
            {values.type === "html" && (
              <RichTextEditor
                value={values.content}
                onChange={(content) => setFieldValue("content", content)}
              />
            )}
            {values.type === "image" && (
              <div style={{ width: 250 }}>
                {values.content && (
                  <img
                    alt="thumbnail"
                    src={`data:image/png;base64,${values.content}`}
                    height={256}
                    style={{ marginBottom: 10 }}
                  />
                )}
                <UploadBase64
                  useCropper
                  removeBase64DataUriPrefix
                  buttonText={intl.formatMessage(messages.upload)}
                  cropperProps={{
                    aspectRatio: 16 / 9,
                    scale: [455, 256],
                  }}
                  onFileReady={(data) => setFieldValue("content", data)}
                />
              </div>
            )}
            {values.type === "video" && vimeoAccessToken && (
              <VimeoUpload
                accessToken={vimeoAccessToken}
                projectId={vimeoArriVideoProjectId}
                onUploadDone={(vimeoVideoId) =>
                  setFieldValue("content", vimeoVideoId)
                }
                videoId={values.content}
                title={values.title}
              />
            )}
            {errors.content && (
              <Form.Text className="text-danger">{errors.content}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={9} lg={9}>
          <Form.Group controlId="formActions" className="mb-3">
            <ButtonToolbar>
              {values.id && (
                <Button
                  variant="danger"
                  className="me-2"
                  onClick={onClickDelete}
                >
                  {intl.formatMessage(genericTranslations.delete)}
                </Button>
              )}
              <Button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
              >
                {intl.formatMessage(genericTranslations.formSubmitLabel)}
              </Button>
            </ButtonToolbar>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

ContentItemForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    content: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  }),
  values: PropTypes.shape({
    content: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  vimeoArriVideoProjectId: PropTypes.string,
};

export default ContentItemForm;
